import { useState, useRef } from 'react';
import { OrderDTO, useOrders } from '../../../hooks/useOrder';
import TitlePage from '../../../components/TitlePage';
import { Column, TableComponent } from '../../../components/TableComponent';
import CreateUpdate from './components/createUpdate';
import { Input } from '@/components/ui/input';
import Delete from './components/delete';
import { Filter, Search } from 'lucide-react';
import { debounce } from 'lodash';
import LoadingCircle from '@/components/LoadingCircle';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import { useGetEmployeeRoleIsSeller } from '@/hooks/useEmployee';
import { Button } from '@/components/ui/button';
import { useProfileContext } from '@/hooks/useProfileContext';
import { useAllCustomers } from '@/hooks/useCustomer';
import {
  checkIsPermissionWithScreen,
  checkIsPermisstionApprove,
  checkIsRoleEditOrders,
  checkIsRoleDeleteOrders,
} from '@/lib/utils';
import ApproveButton from './components/approve';

import { formatCurrency, formatDate } from '@/lib/utils';
import TextUserInactive from '@/components/TextUserInactive';
import DetailOrder from './components/detailOrder';
import OrderStatusRender from './components/orderStatus';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { OrderStatus } from '@/api/orders';
const limit = 10;
const ManageOrders = () => {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';

  const searchRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [employeeId, setEmployeeId] = useState<string | null>(
    isSeller ? profile?.id : null
  );
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [priceSearch, setPriceSearch] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [statusSearch, setStatusSearch] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useOrders({
    page,
    limit,
    search,
    customerId,
    employeeId,
    priceSearch,
    dateRange,
    statusSearch,
  });

  const { data: employeesData } = useGetEmployeeRoleIsSeller(isSeller);
  const { data: customersData } = useAllCustomers();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const columns: Column<OrderDTO>[] = [
    {
      header: 'Mã đơn hàng',
      accessorKey: 'id',
      cell: (item) => {
        return (
          <>
            <DetailOrder order={item} />
          </>
        );
      },
    },
    {
      header: 'Khách hàng',
      accessorKey: 'customer',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900">
            KH-{item.customer.id} - {item.customer.fullName}
          </div>
        );
      },
    },
    {
      header: 'Nhân viên phụ trách',
      accessorKey: 'employee',
      cell: (item) => {
        return (
          <div className="flex flex-col">
            {item.employee.name}{' '}
            <TextUserInactive status={item.employee.status} />
          </div>
        );
      },
    },
    {
      header: 'Ngày bán',
      accessorKey: 'sellDate',
      cell: (item) => {
        return <div className="text-right">{formatDate(item.sellDate)}</div>;
      },
    },
    {
      header: 'Ngày tạo',
      accessorKey: 'createdAt',
      cell: (item) => {
        return <div className="text-right">{formatDate(item.createdAt)}</div>;
      },
    },
    {
      header: 'Người tạo',
      accessorKey: 'createdBy',
      cell: (item) => {
        return <div>{item.createdBy.name}</div>;
      },
    },
    {
      header: 'Số tiền tối đa mua quà',
      accessorKey: 'budgetMaxBuyGift',
      cell: (item) => {
        return (
          <div className="text-right">
            {item.budgetMaxBuyGift
              ? formatCurrency(Number(item.budgetMaxBuyGift))
              : '----'}
          </div>
        );
      },
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => <OrderStatusRender status={item.status} />,
    },
    {
      header: 'Hành động',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {checkIsPermisstionApprove(profile?.role, item.status) && (
              <ApproveButton text="phê duyệt" order={item} />
            )}
            {checkIsPermisstionApprove(profile?.role, item.status) && (
              <ApproveButton text="từ chối" order={item} />
            )}

            {checkIsPermissionWithScreen(
              'orders',
              'update',
              profile?.role?.permissions
            ) &&
              checkIsRoleEditOrders(item.status) && (
                <CreateUpdate
                  order={item}
                  employeesData={employeesData || []}
                  customersData={customersData || []}
                />
              )}
            {checkIsPermissionWithScreen(
              'orders',
              'delete',
              profile?.role?.permissions
            ) &&
              checkIsRoleDeleteOrders(item.status) && (
                <Delete
                  order={item}
                  setPage={() => {
                    if (data?.orders?.length === 1 && page === data?.pages) {
                      setPage(page - 1);
                    }
                  }}
                />
              )}
          </div>
        );
      },
    },
  ];

  const debouncedSearch = debounce((value: string) => {
    setPage(1);
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const listPrice = [
    {
      label: 'Dưới 1 tỷ',
      value: '0-999999999',
    },
    {
      label: '1 tỷ - 3 tỷ',
      value: '1000000000-3000000000',
    },
    {
      label: 'Trên 3 tỷ',
      value: '3000000000',
    },
  ];

  const listStatus = Object.values(OrderStatus).map((status) => ({
    label: <OrderStatusRender status={status} />,
    value: status,
  }));

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'orders',
          'create',
          profile?.role?.permissions
        ) && (
          <CreateUpdate
            employeesData={employeesData || []}
            customersData={customersData || []}
          />
        )}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <div className="flex items-center gap-2 w-full justify-end overflow-x-auto">
          <DatePickerWithRange
            placeholder="Chọn ngày bán"
            date={dateRange}
            setDate={(date) => {
              setDateRange(date);
              if (date?.from && date?.to) {
                setPage(1);
              }
            }}
          />
          <Select
            onValueChange={(value) => {
              setStatusSearch(value);
              setPage(1);
            }}
            value={statusSearch || ''}
          >
            <SelectTrigger className="w-fit luxury-input flex items-center justify-between">
              <SelectValue placeholder="Trạng thái" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {listStatus?.map((status) => (
                <SelectItem
                  key={status.value}
                  value={status.value}
                  className="flex items-center gap-2 justify-center"
                >
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            onValueChange={(value) => {
              setPriceSearch(value);
              setPage(1);
            }}
            value={priceSearch || ''}
          >
            <SelectTrigger className="w-[250px] luxury-input">
              <SelectValue placeholder="Khoảng giá" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {listPrice?.map((price) => (
                <SelectItem key={price.value} value={price.value}>
                  {price.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isSeller && (
            <Select
              onValueChange={(value) => {
                setEmployeeId(value);
                setPage(1);
              }}
              value={employeeId || ''}
            >
              <SelectTrigger className="w-[250px] luxury-input">
                <SelectValue placeholder="Chọn nhân viên phụ trách" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200">
                {employeesData
                  ?.filter((employee) => employee.status === 'active')
                  ?.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      NV-{employee.id} - {employee.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          <Select
            onValueChange={(value) => {
              setCustomerId(value);
              setPage(1);
            }}
            value={customerId || ''}
          >
            <SelectTrigger className="w-[250px] luxury-input">
              <SelectValue placeholder="Chọn khách hàng" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {customersData?.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  KH-{customer.id} - {customer.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Input
            className="w-[300px] pr-10 luxury-input"
            placeholder="Tìm kiếm theo tên sản phẩm"
            onChange={handleSearch}
            ref={searchRef}
          />
          <Search className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-luxury-500" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              if (!isSeller) {
                setEmployeeId(null);
              }
              setDateRange(undefined);
              setPriceSearch(null);
              setPage(1);
              setCustomerId(null);
              setSearch('');
              setStatusSearch(null);
              if (searchRef.current) {
                searchRef.current.value = '';
              }
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="px-6 py-6 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.orders || []}
          columns={columns}
          pageSize={limit}
          totalPages={data?.pages || 0}
          onPageChange={setPage}
          currentPage={page}
          className="luxury-table"
        />
      </div>
    </div>
  );
};

export default ManageOrders;
